import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ServiceCompanyAdmin {
  id: number;
  name: string;
  isActive: boolean;
  updateDate: string | null;
  updateUser: string | null;
}

export interface SaveServiceCompanyRequest {
  name: string;
}

export interface MaterialLookup {
  materialTypeId: number;
  id: number;
  name: string;
  absoluteVolume?: number;
  density?: number;
  specificGravity?: number;
  sackWeight?: number;
  labVolume?: number;
}

/** Mirrors C# MaterialSearchResultDto (additive autosuggest). */
export interface MaterialSearchResult {
  id: number;
  name?: string;
  functionName?: string;
  materialTypeId: number;
  specificGravity: number;
  sackWeight: number;
  density: number;
  absoluteVolume: number;
  addAfterStirring: boolean;
  isFluidLossTestWithFoamNeeded: boolean;
}

@Injectable({ providedIn: 'root' })
export class LookupService {
    
  constructor(private http: HttpClient) {}

  private apiUrl = `${environment.apiUrl}/lookups`;

  getJobTypes(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/getjobtypes`);
  }

  getTestTypes(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/gettesttypes`);
  }

  getJobSubTypes(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/getjobsubtypes`);
  }

  getServiceCompanies(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/getservicecompanies`);
  }

  getAllServiceCompanies(): Observable<ServiceCompanyAdmin[]> {
    return this.http.get<ServiceCompanyAdmin[]>(`${this.apiUrl}/service-companies`);
  }

  createServiceCompany(request: SaveServiceCompanyRequest): Observable<ServiceCompanyAdmin> {
    return this.http.post<ServiceCompanyAdmin>(`${this.apiUrl}/service-companies`, request);
  }

  updateServiceCompany(id: number, request: SaveServiceCompanyRequest): Observable<ServiceCompanyAdmin> {
    return this.http.put<ServiceCompanyAdmin>(`${this.apiUrl}/service-companies/${id}`, request);
  }

  deactivateServiceCompany(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/service-companies/${id}`);
  }

  activateServiceCompany(id: number): Observable<ServiceCompanyAdmin> {
    return this.http.patch<ServiceCompanyAdmin>(`${this.apiUrl}/service-companies/${id}/activate`, null);
  }

  hardDeleteServiceCompany(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/service-companies/${id}/permanent`);
  }

  getRigs(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/getrigstypes`);
  }

  getMudTypes(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/getmudtypes`);
  }

  getSlurryMaterial(): Observable<MaterialLookup[]> {
    return this.http.get<MaterialLookup[]>(`${this.apiUrl}/getslurrymaterials`);
  }

  getMaterials(): Observable<MaterialLookup[]> {
    return this.http.get<MaterialLookup[]>(`${this.apiUrl}/getmaterials`);
  }

  getPozzolanMaterials(): Observable<MaterialLookup[]> {
    return this.http.get<MaterialLookup[]>(`${this.apiUrl}/getpozzolanmaterials`);
  }

  getMaterialsByTypeId(materialTypeId: number): Observable<MaterialLookup[]> {
    return this.http.get<MaterialLookup[]>(`${this.apiUrl}/getmaterialsbytypeid/${materialTypeId}`);
  }

  /**
   * Searches active materials of a given type for the additive autosuggest.
   * materialTypeId: 1 = Dry, 2 = Liquid.
   */
  searchMaterials(criteria: string, materialTypeId: number): Observable<MaterialSearchResult[]> {
    const params = new HttpParams()
      .set('criteria', criteria ?? '')
      .set('materialTypeId', materialTypeId);
    return this.http.get<MaterialSearchResult[]>(`${this.apiUrl}/material-search`, { params });
  }

  getCommonlyUsedEmails(): Observable<CommonlyUsedEmail[]> {
    return this.http.get<CommonlyUsedEmail[]>(`${this.apiUrl}/commonly-used-email`);
  }

  createCommonlyUsedEmail(request: SaveCommonlyUsedEmailRequest): Observable<CommonlyUsedEmail> {
    return this.http.post<CommonlyUsedEmail>(`${this.apiUrl}/commonly-used-email`, request);
  }

  updateCommonlyUsedEmail(id: number, request: SaveCommonlyUsedEmailRequest): Observable<CommonlyUsedEmail> {
    return this.http.put<CommonlyUsedEmail>(`${this.apiUrl}/commonly-used-email/${id}`, request);
  }

  deleteCommonlyUsedEmail(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/commonly-used-email/${id}`);
  }

  getBusinessUnits(): Observable<BusinessUnit[]> {
    return this.http.get<BusinessUnit[]>(`${this.apiUrl}/business-units`);
  }

  searchUsers(firstName: string, lastName: string): Observable<UserSearchResponse> {
    const params = { firstName, lastName };
    return this.http.get<UserSearchResponse>(`${this.apiUrl}/search`, { params });
  }
}

export interface SaveCommonlyUsedEmailRequest {
  email: string;
  name: string;
}

export interface CommonlyUsedEmail {
  id: number;
  email: string;
  name: string;
  updateDate: string;
  updateBy: string;
  isActive: boolean;
}

export interface BusinessUnit {
  sbuCode: string;
  sbuName: string;
}

export interface UserSearchResult {
  id: string;
  displayName: string;
  givenName: string;
  surname: string;
  mail: string | null;
  userPrincipalName: string;
  jobTitle: string | null;
  officeLocation: string | null;
  mobilePhone: string | null;
  preferredLanguage: string | null;
  businessPhones: string | null;
}

export interface UserSearchResponse {
  value: UserSearchResult[];  
}